import { MapPin, Compass, Clock, Truck, MessageCircle } from "lucide-react";
import { openWhatsApp } from "../lib/whatsapp";
import { useT } from "../lib/i18n";

export default function Coverage() {
    const t = useT();

    const towns = [
        "Armoor", "Balkonda", "Bheemgal", "Velpur", "Nandipet", "Mortad",
        "Kammarpally", "Mendora", "Perkit", "Yedapally", "Jakranpally", "Pitlam",
        "Donkeshwar", "Khanapur", "Mupkal", "Dichpally", "Sirikonda", "Birkoor",
    ];

    const stats = [
        { icon: Compass, label: t("Service Radius", "సేవా పరిధి"), value: t("40 km", "40 కిమీ") },
        { icon: Clock, label: t("Same-day visits", "అదే రోజు సందర్శనలు"), value: t("Inside Armoor", "ఆర్మూర్ లోపల") },
        { icon: Truck, label: t("Mandals served", "సేవించిన మండలాలు"), value: "10+" },
        { icon: MapPin, label: t("Base location", "బేస్ లొకేషన్"), value: t("RTC Bus Stand", "RTC బస్ స్టాండ్") },
    ];

    return (
        <div data-testid="page-coverage">
            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16 md:pb-20">
                <div className="max-w-3xl">
                    <p className="eyebrow">{t("Coverage Area", "సేవా ప్రాంతం")}</p>
                    <h1 style={{fontFamily: "'Outfit', 'Noto Sans Telugu', sans-serif"}} className="mt-5 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] text-rvs-navy">
                        {t("Armoor +", "ఆర్మూర్ +")}<br />
                        <span className="text-rvs-orange">{t("40 km", "40 కిమీ")}</span> {t("in every direction.", "ప్రతి దిశలో.")}
                    </h1>
                    <p className="mt-7 text-lg md:text-xl text-[#4A5568] leading-relaxed">
                        {t("Our base is at RTC Bus Stand, Armoor. We serve households and retailers across mandals in Nizamabad district within a 40 km operational radius.", "మా బేస్ ఆర్మూర్ RTC బస్ స్టాండ్‌లో ఉంది. మేము నిజామాబాద్ జిల్లాలోని మండలాలలో 40 కిమీ పరిధిలో గృహాలకు మరియు రిటైలర్‌లకు సేవలు అందిస్తాము.")}
                    </p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
                <div className="grid md:grid-cols-12 gap-8">
                    <div className="md:col-span-7">
                        <div className="rounded-[20px] overflow-hidden border border-rvs-navy-10 bg-white">
                            <iframe
                                title="RVS Electronics Coverage Map"
                                src="https://www.google.com/maps?q=Armoor,+Nizamabad,+Telangana+503224&hl=en&z=10&output=embed"
                                width="100%"
                                height="480"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="coverage-map-iframe"
                            ></iframe>
                        </div>
                    </div>
                    <div className="md:col-span-5">
                        <div className="relative bg-[#0B192C] text-white rounded-[20px] overflow-hidden border border-rvs-navy-10 h-full min-h-[480px] grid-bg p-8 md:p-10 flex flex-col justify-between">
                            <div className="absolute -top-32 -right-32 w-[280px] h-[280px] rounded-full bg-[#FF5A00]/40 blur-[100px]" aria-hidden></div>
                            <div className="relative">
                                <p className="eyebrow text-[#FF5A00]">{t("Operational radius", "సేవా పరిధి")}</p>
                                <h2 style={{fontFamily: "'Outfit', 'Noto Sans Telugu', sans-serif"}} className="mt-3 text-4xl md:text-5xl font-black tracking-tighter leading-[1.05]">
                                    {t("40 km", "40 కిమీ")}<br />{t("from Armoor.", "ఆర్మూర్ నుండి.")}
                                </h2>
                                <p className="mt-5 text-sm text-white/65 leading-relaxed">
                                    {t("A radius wide enough to reach every major village around Armoor — and tight enough to keep our same-day service promise.", "ఆర్మూర్ చుట్టూ ఉన్న ప్రతి ప్రధాన గ్రామానికి చేరగల పరిధి — మరియు అదే రోజు సేవ హామీని నిలబెట్టేంత దగ్గర.")}
                                </p>
                            </div>

                            <div className="relative mt-8 flex items-center justify-center h-44">
                                <div className="absolute w-44 h-44 rounded-full border border-[#FF5A00]/30"></div>
                                <div className="absolute w-32 h-32 rounded-full border border-[#FF5A00]/50"></div>
                                <div className="absolute w-20 h-20 rounded-full border border-[#FF5A00]/70"></div>
                                <div className="relative w-3 h-3 rounded-full bg-[#FF5A00] glow-orange"></div>
                                <span className="absolute top-0 right-2 text-[10px] uppercase tracking-[0.2em] text-white/50">{t("40km", "40కిమీ")}</span>
                                <span className="absolute bottom-1 left-2 text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">{t("Armoor · base", "ఆర్మూర్ · బేస్")}</span>
                            </div>

                            <div className="relative">
                                <button
                                    onClick={() => openWhatsApp(t("Hi RVS Electronics, I want to confirm if my area is covered.", "హాయ్ RVS ఎలక్ట్రానిక్స్, నా ప్రాంతం సేవలో ఉందో లేదో ధృవీకరించాలనుకుంటున్నాను."))}
                                    data-testid="coverage-check-whatsapp-btn"
                                    className="btn-orange w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-semibold"
                                >
                                    <MessageCircle className="w-4 h-4" /> {t("Check my area on WhatsApp", "వాట్సాప్‌లో నా ప్రాంతాన్ని తనిఖీ చేయండి")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-white border-y border-rvs-navy-10">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {stats.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <div key={i} className="flex items-center gap-4">
                                <span className="w-11 h-11 rounded-xl bg-rvs-orange/10 flex items-center justify-center shrink-0">
                                    <Icon className="w-5 h-5 text-rvs-orange" />
                                </span>
                                <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-[#4A5568] font-semibold">{s.label}</p>
                                    <p style={{fontFamily: "'Outfit', 'Noto Sans Telugu', sans-serif"}} className="text-lg md:text-xl font-bold text-rvs-navy">{s.value}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
                <div className="grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-7">
                        <p className="eyebrow">{t("Areas we serve", "మేము సేవించే ప్రాంతాలు")}</p>
                        <h2 style={{fontFamily: "'Outfit', 'Noto Sans Telugu', sans-serif"}} className="mt-4 text-4xl md:text-5xl font-black tracking-tight text-rvs-navy leading-[1.05]">
                            {t("Towns and villages in our network.", "మా నెట్‌వర్క్‌లోని పట్టణాలు మరియు గ్రామాలు.")}
                        </h2>
                    </div>
                    <p className="md:col-span-5 text-base text-[#4A5568] leading-relaxed">
                        {t("This is an indicative list — if your village or mandal is within ~40 km of Armoor, we likely already serve it. Drop us a WhatsApp to confirm.", "ఇది సూచనాత్మక జాబితా — మీ గ్రామం లేదా మండలం ఆర్మూర్ నుండి ~40 కిమీ లోపు ఉంటే, మేము ఇప్పటికే సేవలందిస్తున్నాం. ధృవీకరణకు మాకు వాట్సాప్ చేయండి.")}
                    </p>
                </div>

                <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {towns.map((tw, i) => (
                        <div key={tw} className="card-default p-4 flex items-center gap-2.5 fade-up" style={{animationDelay:`${i*30}ms`}} data-testid={`coverage-town-${i}`}>
                            <MapPin className="w-4 h-4 text-rvs-orange shrink-0" />
                            <span className="text-sm font-semibold text-rvs-navy">{tw}</span>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
